/**
 * Exploration data hooks.
 *
 * Wire shapes here mirror `apps/api/src/exploration/{routes,serialize}.ts`
 * exactly — the API returns camelCase, prefixed ids, and nothing else, so
 * there is nothing to normalize on the way in.
 *
 * Schedules are ordinary automation rules (Decision 4): creating/running one
 * goes through `/org/automation/rules`, this module only reads schedules
 * back for display.
 *
 * @module features/exploration/useExploration
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isActiveExplorationCycleStatus, type ExplorationCycleStatus } from '@chatsdet/types';
import { API_URL } from '../../config';
import { toApiError, parseApiJson } from '../../lib/api-error';

export type { ExplorationCycleStatus } from '@chatsdet/types';

export type ExplorationAssignmentStatus = 'running' | 'completed' | 'failed';

export type ExplorationLeadStatus = 'open' | 'explored' | 'dismissed';

/** The cycle list row — GET /exploration/cycles and the shared prefix of the detail row. */
export type ExplorationCycleRow = {
  id: string;
  status: ExplorationCycleStatus;
  workersPerCycle: number;
  cyclesRemaining: number;
  summaryMd: string | null;
  failureReason: string | null;
  startedAt: string;
  completedAt: string | null;
};

export type ExplorationAssignmentTarget = {
  kind: 'area' | 'lead' | 'seed';
  label: string | null;
};

export type ExplorationAssignment = {
  /** The ad-hoc run's id — also this row's stable key; there is no separate assignment id. */
  runId: string;
  status: ExplorationAssignmentStatus;
  charterMd: string;
  rationale: string;
  target: ExplorationAssignmentTarget;
  persona: { name: string | null } | null;
  run: {
    id: string;
    exploratoryTestId: string | null;
    status: string | null;
    summary: string | null;
    issuesFound: number;
  } | null;
  issues: Array<{ title: string; severity: string | null }>;
  createdAt: string;
  completedAt: string | null;
};

export type ExplorationLead = {
  id: string;
  url: string;
  label: string;
  why: string | null;
  status: ExplorationLeadStatus;
  createdAt: string;
};

export type ExplorationCycleMapChange = {
  kind: 'area' | 'capability' | 'persona_access' | 'lead' | 'rule';
  code: string | null;
  title: string;
  change: 'created' | 'updated' | 'refused' | 'explored' | 'dismissed' | 'deprecated';
  refusedFields?: string[];
  /** Reason for a `refusedFields` entry that needs more than "locked" — keyed by field name. */
  refusedReasons?: Record<string, string>;
  previous?: string | null;
};

/** GET /exploration/cycles/:id */
export type ExplorationCycleDetail = ExplorationCycleRow & {
  propertyId: string;
  dispatchRationaleMd: string | null;
  mapChanges: ExplorationCycleMapChange[];
  assignments: ExplorationAssignment[];
  leads: ExplorationLead[];
};

/** GET /exploration/schedules */
export type ExplorationSchedule = {
  ruleId: string;
  name: string;
  cron: string | null;
  timezone: string | null;
  enabled: boolean;
  nextRunAt: string | null;
  lastRunStatus: string | null;
  workersPerCycle: number | null;
  credentialCount: number;
};

/** Cycle statuses that are still in flight — everything else is terminal. */
export const isActiveCycleStatus = isActiveExplorationCycleStatus;

export function useExplorationSchedules(propertyId?: string, enabled = true) {
  return useQuery({
    queryKey: ['exploration-schedules', propertyId ?? 'all'],
    enabled: Boolean(propertyId) && enabled,
    queryFn: async (): Promise<{ schedules: ExplorationSchedule[] }> => {
      const url = `${API_URL}/exploration/schedules?propertyId=${encodeURIComponent(propertyId!)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch exploration schedules');
      const json = (await res.json()) as { data?: ExplorationSchedule[] };
      return { schedules: Array.isArray(json.data) ? json.data : [] };
    },
  });
}

/**
 * Cycle history for a property. Polls every 5s while any returned cycle is
 * still active, and stops once none are — the *same* observer used by both
 * the home page and the starter's "wait for the new cycle to appear" resolver
 * (there is only one query key per property, so there is only one polling
 * decision to make).
 */
export function useExplorationCycles(propertyId?: string, enabled = true) {
  return useQuery({
    queryKey: ['exploration-cycles', propertyId ?? 'all'],
    enabled: Boolean(propertyId) && enabled,
    queryFn: async (): Promise<{ cycles: ExplorationCycleRow[] }> => {
      const url = `${API_URL}/exploration/cycles?propertyId=${encodeURIComponent(propertyId!)}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch exploration cycles');
      const json = (await res.json()) as { data?: ExplorationCycleRow[] };
      return { cycles: Array.isArray(json.data) ? json.data : [] };
    },
    refetchInterval: (query) => {
      const cycles = query.state.data?.cycles ?? [];
      return cycles.some((c) => isActiveCycleStatus(c.status)) ? 5000 : false;
    },
  });
}

export function useExplorationCycle(cycleId?: string) {
  return useQuery({
    queryKey: ['exploration-cycle', cycleId],
    enabled: Boolean(cycleId),
    queryFn: async (): Promise<{ cycle: ExplorationCycleDetail }> => {
      const res = await fetch(`${API_URL}/exploration/cycles/${cycleId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch exploration cycle');
      const json = (await res.json()) as { data?: ExplorationCycleDetail };
      return { cycle: json.data as ExplorationCycleDetail };
    },
    // Terminal cycles never change; poll only while it might still be in flight.
    refetchInterval: (query) => {
      const status = query.state.data?.cycle?.status;
      return status && isActiveCycleStatus(status) ? 5000 : false;
    },
  });
}

export function useRunExplorationNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: string) => {
      const res = await fetch(`${API_URL}/org/automation/rules/${ruleId}/run`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to start exploration cycle');
      return parseApiJson(res, 'Failed to start exploration cycle') as Promise<unknown>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['exploration-cycles'] });
      qc.invalidateQueries({ queryKey: ['exploration-schedules'] });
    },
  });
}

/**
 * Stop a running cycle. Confirm on the caller side before calling this —
 * there's no undo once the workers are abandoned.
 */
export function useCancelExplorationCycle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (cycleId: string) => {
      const res = await fetch(`${API_URL}/exploration/cycles/${cycleId}/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to cancel exploration cycle');
      const json = (await parseApiJson(res, 'Failed to cancel exploration cycle')) as {
        data: { id: string; workersAbandoned: number };
      };
      return json.data;
    },
    onSuccess: (_data, cycleId) => {
      qc.invalidateQueries({ queryKey: ['exploration-cycles'] });
      qc.invalidateQueries({ queryKey: ['exploration-cycle', cycleId] });
    },
  });
}
